import { useEffect, useState } from 'react';
import axios from 'axios';
import PageHeader from '../../components/layout/PageHeader';
import ContributionPlanForm from '../../components/client/ContributionPlanForm';
import SourceAccountCard from '../../components/client/SourceAccountCard';
import authService from '../../services/auth.service';
import OfferingNotice from '../../components/OfferingNotice';
import fundService from '../../services/fund.service';
import { getApiErrorMessage } from '../../lib/apiError';
import RequestCallButton from '../../components/client/RequestCallButton';
import { CALL_CONTEXT, seriesCallContext } from '../../lib/executiveRefusal';
import { taxFormPath } from '../../lib/taxForm';
import { calendarDate } from '../../components/client/enteringFund.helpers';
import { colors } from '../../styles/theme';
import type {
  BankDetails,
  FundableProduct,
  FundingActivityItem,
  FundingStage,
  FundInstructions,
  PendingFundRequest,
  UnmatchedTransfer,
} from '../../types/finance';
import OnboardingRequiredPanel from '../../components/client/OnboardingRequiredPanel';
import DepositNoticeCard from '../../components/client/DepositNoticeCard';
import { useClientApprovalGate } from '../../hooks/useCurrentUserProfile';
import { readerLocale } from '../../i18n/language';
import {
  ACCEPTED_METHODS_NOTICE,
  SOURCE_ACCOUNT_NOTICE,
  currenciesNeedingSourceAccount,
  productsMissingReference,
  transferReference,
  withIssuedReferences,
} from './fund.helpers';
import { formatCents } from '../../lib/amount';

/* Renamed from `PaymentInstructions.tsx` 2026-05-24. Client copy says
 * "contribution" / "make a contribution" and never "fund" (owner, 2026-10-20: FUND_MODEL_DECISIONS S-22).
 * The action is putting money into the series the client holds a share of,
 * not paying a bill.
 *
 * Self-service is the primary path (2026-09-12): each contracted product has a
 * permanent funding reference the client can use whenever they choose, and a
 * contribution plan they can set up here. Transfers an Account Executive set up
 * for the client are the assisted path and appear below when there are any.
 */

const PAGE_TITLE = 'Make a contribution';
const PAGE_SUBTITLE =
  'Send a transfer whenever you choose, from a bank account registered in your name. Always include the contribution reference for the series so we can match it automatically.';

/** `what` names the value, so each Copy button says what it copies (WCAG 2.4.6, #362). */
function CopyButton({ value, what }: Readonly<{ value: string; what: string }>): JSX.Element {
  const [copied, setCopied] = useState(false);

  function handleCopy(): void {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
    <button
      type="button"
      onClick={handleCopy}
      aria-label={`Copy ${what}`}
      className="ml-2 px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0"
      style={{
        background: copied ? colors.successBg : colors.canvas,
        color: copied ? colors.success : colors.inkSoft,
        border: `1px solid ${copied ? colors.successBorder : colors.border}`,
      }}
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
    {/* The button's name stays "Copy …"; the confirmation is said here. */}
    <output className="sr-only">{copied ? 'Copied!' : ''}</output>
    </>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow({ label, value }: Readonly<DetailRowProps>): JSX.Element {
  return (
    <div
      className="flex items-center justify-between py-3"
      style={{ borderBottom: `1px solid ${colors.borderSoft}` }}
    >
      <span className="text-sm font-medium w-40 flex-shrink-0" style={{ color: colors.muted }}>
        {label}
      </span>
      <div className="flex items-center min-w-0 flex-1 justify-end">
        <span className="font-mono text-sm truncate" style={{ color: colors.ink }}>
          {value}
        </span>
        <CopyButton value={value} what={label} />
      </div>
    </div>
  );
}

function BankDetailsCard({ details }: Readonly<{ details: BankDetails }>): JSX.Element {
  const market = details.market;
  return (
    <div
      className="rounded-2xl p-5 mb-4"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <h2
        className="text-xs font-bold uppercase tracking-widest mb-4"
        style={{ color: colors.inkSoft }}
      >
        Destination account
      </h2>

      <DetailRow label="Beneficiary" value={details.beneficiary} />
      {details.bank_name && <DetailRow label="Bank" value={details.bank_name} />}
      <DetailRow label="Transfer method" value={details.transfer_method} />
      <DetailRow label="Currency" value={details.currency} />

      {market === 'MX' && details.clabe && (
        <DetailRow label="CLABE" value={details.clabe} />
      )}

      {market === 'US' && (
        <>
          {details.routing_number && (
            <DetailRow label="Routing number" value={details.routing_number} />
          )}
          {details.account_number && (
            <DetailRow label="Account number" value={details.account_number} />
          )}
        </>
      )}

      {market === 'CA' && (
        <>
          {details.transit_number && (
            <DetailRow label="Transit" value={details.transit_number} />
          )}
          {details.institution_number && (
            <DetailRow label="Institution" value={details.institution_number} />
          )}
          {details.account_number && (
            <DetailRow label="Account number" value={details.account_number} />
          )}
        </>
      )}

      {/* 0082 — said BEFORE the client sends anything: only a payment that is final
          when it settles is credited, and a payment sent another way goes back at the
          sender's cost. Wording: VALIDAR CON ABOGADO. */}
      <p className="text-xs mt-4 leading-relaxed" style={{ color: colors.inkSoft }}>
        {ACCEPTED_METHODS_NOTICE}
      </p>
    </div>
  );
}

function ReferenceCallout(): JSX.Element {
  return (
    <div
      className="rounded-xl p-4 mb-4"
      style={{
        background: colors.infoBg,
        border: `1px solid ${colors.infoBorder}`,
        color: colors.infoText,
      }}
    >
      <p className="text-sm">
        Enter the reference in the <strong>Concept</strong> or <strong>Memo</strong> field in your
        bank app or online portal. A transfer without the reference takes longer to confirm.
      </p>
      <p className="text-sm mt-2">{SOURCE_ACCOUNT_NOTICE}</p>
    </div>
  );
}

function WaitNote({ children }: Readonly<{ children: React.ReactNode }>): JSX.Element {
  return (
    <div
      className="rounded-lg px-3 py-2"
      style={{
        background: colors.warningBg,
        border: `1px solid ${colors.warning}`,
        color: colors.warning,
      }}
      role="note"
    >
      <p className="text-xs">{children}</p>
    </div>
  );
}

function ReferenceBox({
  label,
  reference,
  what,
}: Readonly<{ label: string; reference: string; what: string }>): JSX.Element {
  return (
    <div>
      <p className="text-xs mb-1" style={{ color: colors.muted }}>
        {label}
      </p>
      <div
        className="flex items-center justify-between px-3 py-2 rounded-lg"
        style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}
      >
        <span className="font-mono text-sm font-semibold" style={{ color: colors.ink }}>
          {reference}
        </span>
        <CopyButton value={reference} what={what} />
      </div>
    </div>
  );
}

function FundingReferenceCard({ product }: Readonly<{ product: FundableProduct }>): JSX.Element {
  const reference = product.funding_reference?.trim() || null;
  // Each fund has its own bank account: the one shown is this product's, as the
  // server resolved it. Without one, no account is shown at all.
  const destination = product.destination;
  return (
    <div
      className="rounded-2xl p-5 mb-3"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <span className="font-semibold text-sm" style={{ color: colors.ink }}>
          {product.product_name}
        </span>
        <span className="font-mono text-xs flex-shrink-0" style={{ color: colors.muted }}>
          {product.currency_code ?? '—'}
        </span>
      </div>

      {/* S-27: the server sends neither the reference nor the account until the client has
          registered the account they will send from; step 1 above is where they do it. */}
      {product.source_account_required && (
        <WaitNote>
          Add the account you&apos;ll send from in step 1 to see how to send your contribution.
        </WaitNote>
      )}
      {/* S-31: a U.S. series shows no account and no reference until the tax form is current. */}
      {product.tax_form_required && !product.source_account_required && (
        <WaitNote>
          Review and sign your U.S. tax form to see how to send your contribution.{' '}
          <a href={taxFormPath(product.product_id)} style={{ color: colors.ink, fontWeight: 600 }}>
            Review my tax form →
          </a>
        </WaitNote>
      )}
      {/* Fail safe: never show the account to send to without a reference the
          transfer can be matched on, nor the account of another product. */}
      {!reference && !product.source_account_required && !product.tax_form_required && (
        <WaitNote>
          We&apos;re preparing your contribution reference for this series. Please don&apos;t send money until it appears here.
        </WaitNote>
      )}
      {reference && (
        <>
          <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: colors.inkSoft }}>
            Step 2 of 2
          </p>
          <ReferenceBox
            label="Your contribution reference — use it on every transfer"
            reference={reference}
            what="Contribution reference"
          />
          <div className="mt-4">
            {destination ? (
              <BankDetailsCard details={destination} />
            ) : (
              <>
                <WaitNote>
                  We&apos;re confirming the account for transfers to this series. Please contact your Account Executive before sending money.
                </WaitNote>
                <RequestCallButton
                  productId={product.product_id}
                  context={CALL_CONTEXT.accountBeingConfirmed}
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SectionTitle({ children }: Readonly<{ children: React.ReactNode }>): JSX.Element {
  return (
    <h2
      className="text-sm font-bold uppercase tracking-widest mb-3"
      style={{ color: colors.inkSoft }}
    >
      {children}
    </h2>
  );
}

function FundingReferencesSection({
  data,
  referenceError,
}: Readonly<{ data: FundInstructions; referenceError: string | null }>): JSX.Element {
  const products = data.fundable_products ?? [];
  return (
    <section>
      <SectionTitle>Your series</SectionTitle>
      {referenceError && (
        <div
          className="rounded-lg p-3 mb-3 text-sm"
          role="alert"
          style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}
        >
          {referenceError}
          <div>
            <RequestCallButton context={CALL_CONTEXT.referenceNotIssued} />
          </div>
        </div>
      )}
      {products.length === 0 ? (
        <div
          className="rounded-2xl p-6 text-sm"
          style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
        >
          You can make a contribution to a series once you have signed its contract.{' '}
          <a href="/client/portfolio" style={{ color: colors.ink, fontWeight: 600 }}>
            See available series →
          </a>
        </div>
      ) : (
        products.map((p) => (
          <FundingReferenceCard key={p.product_id} product={p} />
        ))
      )}
      <p className="text-xs mt-2" style={{ color: colors.mutedSoft }}>
        These banking details are always current. Do not reuse details from previous emails or
        documents.
      </p>
    </section>
  );
}

function PendingFundRequestCard({
  deposit,
}: Readonly<{ deposit: PendingFundRequest }>): JSX.Element {
  const formatted = formatCents(deposit.amount);

  const dateLabel = new Date(deposit.created_at).toLocaleDateString(readerLocale());
  // A due date is a calendar day, not an instant: read as one it showed the day before
  // to every client west of Greenwich.
  const dueLabel = deposit.expected_due_date ? calendarDate(deposit.expected_due_date) : null;

  // The account is embedded per transfer: it is the account of the fund that
  // receives THIS product, so two transfers can name two different accounts.
  const destination = deposit.destination;

  // Fail safe: only invite a transfer once the reference exists. Without
  // it the wire can't reconcile, so we withhold the destination account
  // and tell the client to wait rather than send money with no memo.
  // (reference_number is NOT NULL in the DB, so this is normally set.)
  const reference = transferReference(deposit);
  const awaitingSourceAccount = deposit.source_account_required === true;
  const awaitingTaxForm = deposit.tax_form_required === true && !awaitingSourceAccount;

  return (
    <div
      className="rounded-2xl p-5 mb-3"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <span className="font-semibold text-sm" style={{ color: colors.ink }}>
          {deposit.product_name}
        </span>
        <span className="font-mono text-sm font-semibold flex-shrink-0" style={{ color: colors.inkSoft }}>
          {deposit.currency_code} {formatted}
        </span>
      </div>

      {awaitingSourceAccount && (
        <WaitNote>
          Add the account you&apos;ll send from in step 1 to see how to send this transfer.
        </WaitNote>
      )}
      {awaitingTaxForm && (
        <WaitNote>
          Review and sign your U.S. tax form to see how to send this transfer.{' '}
          <a href={taxFormPath()} style={{ color: colors.ink, fontWeight: 600 }}>
            Review my tax form →
          </a>
        </WaitNote>
      )}
      {!awaitingSourceAccount && !awaitingTaxForm && reference ? (
        <>
          <ReferenceBox label="Reference number" reference={reference} what="Reference number" />
          <div className="mt-4">
            {destination ? (
              <BankDetailsCard details={destination} />
            ) : (
              <>
                <WaitNote>
                  We&apos;re confirming the account for this transfer. Please contact your Account Executive before sending money.
                </WaitNote>
                <RequestCallButton
                  context={seriesCallContext(CALL_CONTEXT.accountBeingConfirmed, deposit.product_name)}
                />
              </>
            )}
          </div>
        </>
      ) : (
        !awaitingSourceAccount && !awaitingTaxForm && (
          <WaitNote>
            We&apos;re finalizing the reference number for this transfer. Please don&apos;t send money yet — without the reference we can&apos;t match your transfer to your account.
          </WaitNote>
        )
      )}

      <div className="mt-3 text-xs grid grid-cols-1 sm:grid-cols-2 gap-2"
           style={{ color: colors.mutedSoft }}>
        {dueLabel && (
          <div>
            <span style={{ color: colors.muted }}>Due by: </span>
            <span style={{ color: colors.ink }}>{dueLabel}</span>
          </div>
        )}
        <div className={dueLabel ? '' : 'sm:col-span-2'}>
          <span style={{ color: colors.muted }}>Created: </span>
          <span>{dateLabel}</span>
        </div>
      </div>
    </div>
  );
}

/** Transfers an Account Executive set up for the client (the assisted path).
 *  Hidden entirely when there are none: it is no longer how funding starts. */
function AssistedTransfersSection({ data }: Readonly<{ data: FundInstructions }>): JSX.Element | null {
  if (data.pending_deposits.length === 0) return null;
  return (
    <section className="mt-8">
      <SectionTitle>Transfers set up with your Account Executive</SectionTitle>
      {data.pending_deposits.map((d) => (
        <PendingFundRequestCard key={d.deposit_request_id} deposit={d} />
      ))}
    </section>
  );
}

const STAGE_META: Record<FundingStage, { label: string; bg: string; border: string; fg: string }> = {
  awaiting_transfer: {
    label: 'Awaiting your transfer',
    bg: colors.canvas, border: colors.border, fg: colors.muted,
  },
  received: {
    label: 'Transfer received — confirming',
    bg: colors.infoBg, border: colors.infoBorder, fg: colors.infoText,
  },
  confirmed: {
    label: 'Confirmed',
    bg: colors.successBg, border: colors.successBorder, fg: colors.success,
  },
  rejected: {
    label: 'Not credited',
    bg: colors.dangerBg, border: colors.dangerBorder, fg: colors.danger,
  },
};

function StageBadge({ stage }: Readonly<{ stage: FundingStage }>): JSX.Element {
  const m = STAGE_META[stage];
  return (
    <span
      className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
      style={{ background: m.bg, border: `1px solid ${m.border}`, color: m.fg }}
    >
      {m.label}
    </span>
  );
}

/** The most-advanced timestamp the client should anchor on: confirmed,
 *  else received, else created. A dated trail reassures without a timer. */
function fundingTrail(item: FundingActivityItem): string {
  const on = (iso: string): string => new Date(iso).toLocaleDateString(readerLocale());
  if (item.confirmed_at) return `Confirmed ${on(item.confirmed_at)}`;
  if (item.received_at) return `Received ${on(item.received_at)}`;
  return `Requested ${on(item.created_at)}`;
}

function FundingActivityRow({ item }: Readonly<{ item: FundingActivityItem }>): JSX.Element {
  const formatted = formatCents(item.amount);

  const trail = fundingTrail(item);

  return (
    <div
      className="rounded-xl p-4 mb-2 flex items-start justify-between gap-4"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="min-w-0">
        <p className="font-semibold text-sm truncate" style={{ color: colors.ink }}>
          {item.product_name}
        </p>
        <p className="text-xs mt-0.5" style={{ color: colors.mutedSoft }}>
          {trail}
          {item.reference_number ? ` · ref ${item.reference_number}` : ''}
        </p>
        {item.stage === 'rejected' && item.rejection_reason && (
          <p className="text-sm mt-2 whitespace-pre-wrap" style={{ color: colors.inkSoft }}>
            <span className="font-semibold">Reason: </span>
            {item.rejection_reason}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <span className="font-mono text-sm font-semibold" style={{ color: colors.inkSoft }}>
          {item.currency_code} {formatted}
        </span>
        <StageBadge stage={item.stage} />
      </div>
    </div>
  );
}

function FundingActivitySection({ items }: Readonly<{ items: FundingActivityItem[] }>): JSX.Element | null {
  // Hide rows that are still purely "awaiting your transfer" — those are
  // already covered by the transfers section above, with the bank details and
  // reference. This timeline is about what happens AFTER the client sends money
  // (received → confirmed), so a not-yet-sent row here would just duplicate it.
  const visible = items.filter((i) => i.stage !== 'awaiting_transfer');
  if (visible.length === 0) return null;

  return (
    <section className="mt-8">
      <SectionTitle>Contribution activity</SectionTitle>
      <p className="text-xs mb-3" style={{ color: colors.muted }}>
        Once you send a transfer it appears here as we receive and confirm it. Confirmation can take a little time after the money arrives.
      </p>
      {visible.map((i) => (
        <FundingActivityRow key={i.deposit_request_id} item={i} />
      ))}
    </section>
  );
}

function UnmatchedTransfersSection({ items }: Readonly<{ items: UnmatchedTransfer[] }>): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <section className="mb-6">
      <SectionTitle>Transfers we need to review</SectionTitle>
      {items.map((t) => (
        <div
          key={t.bank_transaction_id}
          className="rounded-xl p-4 mb-2"
          role="note"
          style={{ background: colors.warningBg, border: `1px solid ${colors.warning}` }}
        >
          <div className="flex items-start justify-between gap-4">
            <p className="font-semibold text-sm" style={{ color: colors.ink }}>
              {t.product_name}
            </p>
            <span className="font-mono text-sm font-semibold flex-shrink-0" style={{ color: colors.inkSoft }}>
              {t.currency_code} {formatCents(t.amount)}
            </span>
          </div>
          <p className="text-xs mt-1" style={{ color: colors.inkSoft }}>
            Received {new Date(t.received_at).toLocaleDateString(readerLocale())}. {t.message}
          </p>
          {t.reason_code === 'sender_not_registered' && (
            <a href="#source-account" className="text-xs font-semibold" style={{ color: colors.ink }}>
              Add the account you send from →
            </a>
          )}
        </div>
      ))}
    </section>
  );
}

const REFERENCE_ISSUE_ERROR =
  'We could not create a contribution reference for every series. Reload the page, or contact your Account Executive if it keeps happening.';

/** Issue the funding reference of any contracted product still missing one.
 *  Idempotent server-side, so a reload or a second tab never creates a second
 *  reference. Reports whether any issuance failed instead of hiding it. */
async function issueMissingReferences(
  clientId: string,
  data: FundInstructions,
): Promise<{ data: FundInstructions; failed: boolean }> {
  const missing = productsMissingReference(data);
  if (missing.length === 0) return { data, failed: false };
  const settled = await Promise.allSettled(
    missing.map((p) => fundService.issueFundingReference(clientId, p.product_id)),
  );
  const issued = settled.flatMap((s) => (s.status === 'fulfilled' ? [s.value] : []));
  return { data: withIssuedReferences(data, issued), failed: issued.length < missing.length };
}

/** Step 1 is shown for every currency the client contributes in: open while an account is
 *  missing, a one-line summary once it is registered. */
function currenciesToRegisterFor(data: FundInstructions): string[] {
  const all = (data.fundable_products ?? []).map((p) => p.currency_code);
  return [
    ...new Set([...currenciesNeedingSourceAccount(data), ...all.filter((c): c is string => !!c)]),
  ];
}

function loadErrorMessage(e: unknown): string {
  if (axios.isAxiosError(e) && e.response?.status === 422) {
    return 'Your market could not be determined. Please complete your profile first.';
  }
  return getApiErrorMessage(e, 'Could not load contribution instructions.');
}

export default function Fund(): JSX.Element {
  const gate = useClientApprovalGate();
  const [clientId, setClientId] = useState<string | null>(null);
  const [data, setData] = useState<FundInstructions | null>(null);
  const [activity, setActivity] = useState<FundingActivityItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [referenceError, setReferenceError] = useState<string | null>(null);
  const [unmatched, setUnmatched] = useState<UnmatchedTransfer[]>([]);
  const [activityError, setActivityError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip the fetch until the gate resolves to `approved`. The backend
    // would 422 for a not-yet-approved client (no market resolvable) and
    // 403 / give-empty for staff — neither path renders here.
    if (gate !== 'approved') {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const id = await authService.getMyId();
        const result = await fundService.getFundInstructions(id);
        const withReferences = await issueMissingReferences(id, result);
        if (cancelled) return;
        setClientId(id);
        setData(withReferences.data);
        setReferenceError(withReferences.failed ? REFERENCE_ISSUE_ERROR : null);
        // Funding activity must not blank the page, so it is fetched
        // separately. It is no longer silent on failure: it carries the
        // transfers that did not match, which the client needs to know about.
        try {
          const act = await fundService.getFundingActivity(id);
          if (!cancelled) {
            setActivity(act.items);
            setUnmatched(act.unmatched_transfers ?? []);
            setActivityError(false);
          }
        } catch {
          if (!cancelled) setActivityError(true);
        }
      } catch (e: unknown) {
        if (!cancelled) setError(loadErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gate, reloadKey]);

  if (gate === 'loading') {
    return (
      <>
        <PageHeader title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
        <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
      </>
    );
  }

  if (gate === 'pending') {
    return (
      <>
        <PageHeader title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
        <OnboardingRequiredPanel feature="Making a contribution" />
      </>
    );
  }

  return (
    <>
      <PageHeader title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />

      {loading && (
        <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
      )}

      {!loading && error && (
        <div
          className="rounded-lg p-4 mb-6 text-sm"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
          role="alert"
        >
          {error}
        </div>
      )}

      {!loading && data && (
        <>
          <UnmatchedTransfersSection items={unmatched} />
          {activityError && (
            <p className="text-xs mb-4" role="alert" style={{ color: colors.danger }}>
              We could not load your recent transfers. Reload the page to see their status.
            </p>
          )}
          {clientId && (
            <div id="source-account">
              {currenciesToRegisterFor(data).map((currency) => (
                <SourceAccountCard
                  key={currency}
                  clientId={clientId}
                  currency={currency}
                  onRegistered={() => setReloadKey((k) => k + 1)}
                />
              ))}
            </div>
          )}
          <ReferenceCallout />
          <FundingReferencesSection data={data} referenceError={referenceError} />

          {/* Owner, 2026-10-31: the client tells us about a transfer they sent; it moves no
              money and helps us match it (a registered account, the exact amount, a few days). */}
          {clientId && (
            <DepositNoticeCard
              clientId={clientId}
              series={(data.fundable_products ?? []).filter((p) => !p.source_account_required)}
              audience="client"
            />
          )}

          {clientId && (data.fundable_products ?? []).length > 0 && (
            <section id="plan" className="mt-8">
              <SectionTitle>Contribution plan</SectionTitle>
              <p className="text-xs mb-3" style={{ color: colors.muted }}>
                Prefer to contribute on a schedule? Set up a plan and we will remind you before
                each contribution is due. You can still send extra transfers at any time.
              </p>
              <ContributionPlanForm clientId={clientId} products={data.fundable_products} />
            </section>
          )}

          <AssistedTransfersSection data={data} />
          <FundingActivitySection items={activity} />
        </>
      )}
      <OfferingNotice />
    </>
  );
}
